import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { PageFlip } from 'page-flip';
import { FileUpload } from '@models/file';
import { GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf.mjs';
import {
  getDocument,
  PDFDocumentProxy,
  PDFPageProxy,
} from 'pdfjs-dist/legacy/build/pdf.mjs';
import { BehaviorSubject } from 'rxjs';
import { delay, filter } from 'rxjs/operators';
import { component } from '@shared/grade-unit/grade-unit-routing.module';

@Component({
  selector: 'app-page-turner',
  styleUrls: ['./page-turner.component.scss'],
  template: `
    <div class="pager-area">
      <div class="col justify-center align-items-centered">
        <mat-spinner *ngIf="loading"></mat-spinner>
        <div class="flip-book" id="page-turner">
          <ng-container *ngFor="let url of sortedUrls$ | async">
            <div class="page">
              <img [src]="url" alt="" />
            </div>
          </ng-container>
        </div>
      </div>
      <div class="center">
        <button mat-button color="primary" (click)="emitClose.emit()">
          Close Book
        </button>
      </div>
    </div>
  `,
})
export class PageTurnerComponent implements OnInit {
  @Input() file: FileUpload = new FileUpload();
  @Output() emitClose = new EventEmitter();
  private pageFlip: PageFlip;
  private totalPages: number = 100;
  private height: number = null;
  private width: number = null;
  private pageWidth: number = 0;
  private pageHeight: number = 0;
  private flipBox: { height: number; width: number } = { height: 0, width: 0 };
  public dataUrls$: BehaviorSubject<{ url: string; page: number }[]> =
    new BehaviorSubject([]);
  public sortedUrls$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  public loading: boolean = false;

  public get dataUrls() {
    return this.dataUrls$.getValue().sort((url) => url.page);
  }

  public constructor(private elementRef: ElementRef) {
    this.loading = true;
    GlobalWorkerOptions.workerSrc =
      'https://unpkg.com/pdfjs-dist@4.6.82/legacy/build/pdf.worker.min.mjs';
  }

  public ngOnInit() {
    this.getDimensions();
    this.setupPDFPages();
    this.dataUrls$
      .pipe(
        filter((urls) => urls.length === this.totalPages),
        delay(2000),
      )
      .subscribe((urls) => {
        if (urls.length === this.totalPages) {
          this.pageFlip = new PageFlip(document.getElementById('page-turner'), {
            width: this.flipBox.width, // base page width
            height: this.flipBox.height, // base page height

            size: 'stretch',
            maxShadowOpacity: 0.5, // Half shadow intensity
            showCover: true,
            mobileScrollSupport: false, // disable content scrolling on mobile devices
          });
          this.loading = false;
          const sortedUrls = urls
            .sort((a, b) => (a.page > b.page ? 1 : -1))
            .map((page) => page.url);
          this.sortedUrls$.next(sortedUrls);
          setTimeout(() => {
            const page = document.querySelectorAll('.page');
            if (page?.length > 0)
              this.pageFlip.loadFromHTML(document.querySelectorAll('.page'));
          }, 300);
        }
      });
  }

  private getDimensions() {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    this.width = rect.width / 2;
    this.height = rect.height - 50;
  }

  private setupPDFPages() {
    const loadingTask = getDocument(this.file.cdnUrl) as any;

    loadingTask.promise.then((pdf: PDFDocumentProxy) => {
      // Fetch the first page
      this.totalPages = pdf.numPages;
      for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
        const selectedPage = pdf.getPage(pageNumber) as any;

        selectedPage.then((page: PDFPageProxy) => {
          const viewport = page.getViewport({ scale: 2 });
          if (!this.pageHeight && !this.pageWidth) {
            this.pageHeight = viewport.height;
            this.pageWidth = viewport.width;
            const pageRatio = this.pageWidth / this.pageHeight;
            const componentRatio = this.width / this.height;
            if (componentRatio >= pageRatio) {
              this.flipBox.height = this.height;
              this.flipBox.width =
                (this.width * (this.pageWidth * this.height)) /
                (this.pageHeight * this.width);
            } else {
              this.flipBox.width = this.width;
              this.flipBox.height =
                (this.height * (this.width * this.pageHeight)) /
                (this.height * this.pageWidth);
            }
          }

          // Prepare canvas using PDF page dimensions
          const canvas = document.createElement('canvas') as HTMLCanvasElement;
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          // Render PDF page into canvas context
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };

          const renderTask = page.render(renderContext);
          renderTask.promise.then(() => {
            const urls = this.dataUrls;
            const dataUrl = canvas.toDataURL('image/png', 1);
            canvas.remove();
            urls.push({ url: dataUrl, page: pageNumber });
            this.dataUrls$.next(urls);
          });
        });
      }
    });
  }
}
